import { useState, useMemo } from "react";
import { Popover, Checkbox } from "antd";
import { useTranslation } from "react-i18next";
import { BsDatabaseExclamation, BsThreeDots, BsArrowUp, BsArrowDown } from "react-icons/bs";
import { cn } from "../utils/functions";
import Menu from "./Menu";

const Table = ({
  columns,
  data,
  loading = false,
  loadingUiSize = 5,
  scroll = {},
  headClass,
  nestedRowKey,
  showFilters = false,
  showSearch = false,
  showViewToggle = false,
  componentTitle = "",
  rowSelection,
  onRow,
  sortConfig: externalSortConfig,
  onSort,
}) => {
  const { t } = useTranslation();
  const [internalSortConfig, setInternalSortConfig] = useState({ key: null, direction: null });
  const [openPopoverItem, setOpenPopoverItem] = useState(null);
  const sortConfig = externalSortConfig || internalSortConfig;

  const sortedData = useMemo(() => {
    if (onSort || !sortConfig.key || !sortConfig.direction || !data) return data;

    const column = columns.find((c) => (c.key || c.dataIndex) === sortConfig.key);
    if (!column) return data;

    return [...data].sort((a, b) => {
      let valA, valB;

      // Helper to extract value
      const getValue = (item, col) => {
        let val;
        if (col.dataIndex) {
          if (Array.isArray(col.dataIndex)) {
            val = col.dataIndex.reduce((acc, curr) => acc?.[curr], item);
          } else {
            val = item[col.dataIndex];
          }
        } else {
          val = item;
        }

        if (col.renderCsv) {
          return col.renderCsv(val, item);
        }
        return val;
      };

      valA = getValue(a, column);
      valB = getValue(b, column);

      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, columns]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
    }
    
    if (onSort) {
      onSort({ key, direction });
    } else {
      setInternalSortConfig({ key, direction });
    }
  };

  let nestedRow = null;

  // Show skeleton when loading and no data
  if (loading && (!data || data.length === 0)) {
    return (
      <TableSkeleton
        loadingUiSize={loadingUiSize}
        columnsCount={columns?.length || 5}
        showFilters={showFilters}
        showSearch={showSearch}
        showViewToggle={showViewToggle}
        componentTitle={componentTitle}
      />
    );
  }

  const renderItem = (item, column) => {
    if (column?.key === "action" && column?.render) {
      return (
        <Popover
          content={
            <div onClick={() => setOpenPopoverItem(null)}>
              <Menu items={column.render(item)} />
            </div>
          }
          placement="bottomRight"
          arrow={false}
          trigger="click"
          open={openPopoverItem === item}
          onOpenChange={(open) => setOpenPopoverItem(open ? item : null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <BsThreeDots className="cursor-pointer text-base mr-2" />
          </div>
        </Popover>
      );
    } else if (
      column?.dataIndex &&
      Object.prototype.hasOwnProperty.call(item, column?.dataIndex)
    ) {
      if (column.render)
        return column.render(item[column?.dataIndex], item) || "-";
      else if (typeof item[column?.dataIndex] === "number") {
        return item[column?.dataIndex];
      } else {
        return item[column?.dataIndex] ? item[column?.dataIndex] : "-";
      }
    } else if (column.render && !column?.dataIndex) {
      return column.render(item) || "-";
    }
    return "-";
  };

  const renderNestedRow = (nestedItem, column) => {
    if (column.renderNested)
      return column.renderNested(nestedItem[column.nestDataIndex]) || "-";
  };

  return (
    <div className="tableContainer tableScrollBar overflow-y-auto w-full">
      <div
        style={{
          maxHeight: scroll.y ? `${scroll.y}px` : "auto",
        }}>
        <table
          className=""
          style={{
            width: "100%",
          }}>
          <thead
            className={cn(
              "font-Popins text-black/70 bg-white border-b border-gray-200 sticky top-0 z-10 tracking-wider",
              { [headClass]: headClass }
            )}>
            <tr>
              {rowSelection && (
                <th className="py-[14px] pl-3 text-left whitespace-nowrap tracking-wide rounded-tl-lg w-[50px]">
                  <Checkbox
                    checked={
                      data?.length > 0 &&
                      rowSelection.selectedRowKeys?.length === data.length
                    }
                    indeterminate={
                      rowSelection.selectedRowKeys?.length > 0 &&
                      rowSelection.selectedRowKeys?.length < data?.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        rowSelection.onChange(
                          data.map((item) => item.id)
                        );
                      } else {
                        rowSelection.onChange([]);
                      }
                    }}
                  />
                </th>
              )}
              {columns.map((column, index) => {
                const colKey = column.key || column.dataIndex || `column-${index}`;
                const isSortable = column.title && column.key !== "action";
                return (
                  <th
                    key={colKey}
                    onClick={() => isSortable && handleSort(colKey)}
                    className={`py-[14px] pl-3 text-left whitespace-nowrap tracking-wide ${index === 0 && !rowSelection ? "rounded-tl-lg" : ""
                      } ${index === columns.length - 1 ? "rounded-tr-lg" : ""} ${isSortable ? "cursor-pointer select-none hover:bg-gray-50" : ""
                      }`}
                    style={{
                      width: column.width,
                      minWidth: column.width,
                      ...(column.style || {})
                    }}>
                    <div className="flex items-center gap-1">
                      {column.title || null}
                      {isSortable && (
                        <span className="text-gray-400 text-xs flex flex-col ml-1">
                          {sortConfig.key === colKey && sortConfig.direction === "asc" ? (
                            <BsArrowUp className="text-blue-600" />
                          ) : sortConfig.key === colKey && sortConfig.direction === "desc" ? (
                            <BsArrowDown className="text-blue-600" />
                          ) : (
                            <div className="flex flex-col -space-y-1 opacity-30 group-hover:opacity-100">
                              <BsArrowUp className="text-[10px]" />
                              <BsArrowDown className="text-[10px]" />
                            </div>
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          {sortedData?.length > 0 && !loading && (
            <>
              {sortedData.map((item, index) => {
                const isNest = Boolean(
                  nestedRowKey &&
                  item[nestedRowKey] &&
                  item[nestedRowKey].length > 1
                );

                if (isNest) {
                  nestedRow = item[nestedRowKey];
                }
                let nestContent = null;
                if (nestedRow) {
                  nestContent = nestedRow.map((nestedItem, nestedIndex) => {
                    if (nestedIndex === 0) {
                      return null;
                    }
                    return (
                      <tr
                        key={nestedIndex}
                        className={`${nestedIndex === nestedRow.length - 1
                          ? "border-b"
                          : "border-b border-b-slate-900/20"
                          }`}>
                        {columns.map((column, colIndex) => {
                          if (
                            column?.nestDataIndex &&
                            Object.prototype.hasOwnProperty.call(
                              nestedItem,
                              column?.nestDataIndex
                            )
                          ) {
                            return (
                              <td
                                key={column.key || column.dataIndex || `nested-${nestedIndex}-${colIndex}`}
                                className={cn("py-2 pl-3 whitespace-nowrap", {
                                  [column.tdClass]: column.tdClass,
                                })}>
                                {renderNestedRow(nestedItem, column)}
                              </td>
                            );
                          } else return null;
                        })}
                      </tr>
                    );
                  });
                }
                const isSelected = rowSelection?.selectedRowKeys?.includes(item.id);
                const rowProps = onRow ? onRow(item) : {};
                const rowCustomBg = rowProps?.style?.backgroundColor;

                return (
                  <tbody
                    style={{
                      transition: "background-color 0.2s ease",
                      backgroundColor: isSelected ? "#e6f4ff" : (rowCustomBg || "transparent"),
                    }}
                    key={index}>
                    <tr
                      {...(onRow ? {
                        ...rowProps,
                        onClick: (e) => {
                          const interactiveElements = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL'];
                          if (
                            interactiveElements.includes(e.target.tagName) ||
                            e.target.closest('button, a, input, select, textarea, label, .ant-popover, .ant-dropdown')
                          ) {
                            return;
                          }
                          rowProps.onClick && rowProps.onClick(e);
                        }
                      } : {})}
                      style={{
                        backgroundColor: isSelected ? "#e6f4ff" : (rowCustomBg || "inherit"),
                        ...(rowProps?.style || {})
                      }}
                      className={cn(onRow ? "cursor-pointer" : "", rowProps?.className || "", {
                        "border-b": index !== data.length - 1,
                        "border-b border-b-slate-900/10":
                          isNest && index !== data.length - 1,
                      })}>
                      {rowSelection && (
                        <td 
                          style={{ backgroundColor: isSelected ? "#e6f4ff" : (rowCustomBg || "inherit") }}
                          className="py-3 px-3 pl-3 whitespace-nowrap rounded-bl-lg">
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              const selected = new Set(
                                rowSelection.selectedRowKeys
                              );
                              if (e.target.checked) {
                                selected.add(item.id);
                              } else {
                                selected.delete(item.id);
                              }
                              rowSelection.onChange(Array.from(selected));
                            }}
                          />
                        </td>
                      )}
                      {columns.map((column, colIndex) => {
                        const isNest = Boolean(
                          nestedRowKey &&
                          item[nestedRowKey] &&
                          item[nestedRowKey].length > 1
                        );
                        const isSetRowSpan = Boolean(
                          isNest && !column.renderNested
                        );
                        return (
                          <td
                            rowSpan={
                              isSetRowSpan ? item[nestedRowKey].length : 1
                            }
                            key={column.key || column.dataIndex || `cell-${index}-${colIndex}`}
                            style={{
                              width: column.width,
                              minWidth: column.width,
                              backgroundColor: isSelected ? "#e6f4ff" : (rowCustomBg || "inherit"),
                              ...(column.style || {})
                            }}
                            className={cn(
                              "py-3 px-3 pl-3 whitespace-nowrap",
                              {
                                "rounded-bl-lg":
                                  index === data.length - 1 && colIndex === 0 && !rowSelection,
                                "rounded-br-lg":
                                  index === data.length - 1 &&
                                  colIndex === columns.length - 1,
                                [column.tdClass]: column.tdClass,
                              }
                            )}>
                            {renderItem(item, column)}
                          </td>
                        );
                      })}
                    </tr>
                    {nestContent || null}
                  </tbody>
                );
              })}
            </>
          )}
          {loading && data?.length > 0 && (
            <TableLoader length={loadingUiSize} columnsCount={columns?.length || 5} />
          )}
        </table>
        {!data?.length && !loading && (
          <div
            colSpan={columns.length}
            className="flex flex-col justify-center items-center h-full py-10">
            <BsDatabaseExclamation className="text-slate-200" size={70} />
            <span className="py-2 text-lg text-slate-500">{t("table.noData")}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Table;

const TableLoader = ({ length = 3, columnsCount = 5 }) => {
  const loaderArray = Array(length).fill(null);
  const skeletonColumns = Array(columnsCount).fill(null);

  return (
    <tbody>
      {loaderArray.map((_, rowIndex) => (
        <tr
          key={rowIndex}
          className="bg-white hover:bg-gray-50 border-b border-gray-100">
          {skeletonColumns.map((_, colIndex) => (
            <td key={colIndex} className="py-3 px-3 whitespace-nowrap">
              <div
                className={`h-4 bg-gray-200 rounded animate-pulse ${colIndex === 0
                  ? "w-32"
                  : colIndex === skeletonColumns.length - 1
                    ? "w-20"
                    : "w-24"
                  }`}
                style={{
                  animationDelay: `${rowIndex * 0.1 + colIndex * 0.05}s`,
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
};

const TableSkeleton = ({
  loadingUiSize = 10,
  columnsCount = 5,
  showFilters = true,
  showSearch = true,
  showViewToggle = true,
  componentTitle = "Loading...",
}) => {
  const skeletonRows = Array(loadingUiSize).fill(null);
  const skeletonColumns = Array(columnsCount).fill(null);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Top Navigation Bar Skeleton */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center justify-between px-4 py-2 gap-2">
          {/* Left Side - Title Skeleton */}
          <div className="flex items-center gap-4">
            <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
          </div>

          {/* Right Side - Actions Skeleton */}
          <div className="flex items-center gap-2">
            <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
            <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </div>

      {/* Filters & View Controls Bar Skeleton */}
      <div className="flex flex-wrap items-center justify-between px-4 py-3 gap-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        {/* Left - Filters Skeleton */}
        <div className="flex items-center gap-2 flex-1">
          {/* Search Skeleton */}
          {showSearch && (
            <div className="w-64">
              <div className="h-8 bg-gray-200 rounded animate-pulse" />
            </div>
          )}

          {/* Filters Skeleton */}
          {showFilters && (
            <div className="flex items-center gap-2">
              <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
              <div className="h-8 w-28 bg-gray-200 rounded animate-pulse" />
              <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
            </div>
          )}
        </div>

        {/* Right - View & Export Controls Skeleton */}
        <div className="flex items-center gap-2">
          {/* View Toggle Skeleton */}
          {showViewToggle && (
            <div className="flex bg-gray-100 rounded p-1">
              <div className="flex items-center gap-1 px-3 py-1">
                <div className="w-4 h-4 bg-gray-300 rounded" />
                <span className="text-xs text-gray-300">Card</span>
              </div>
              <div className="flex items-center gap-1 px-3 py-1 bg-white rounded">
                <div className="w-4 h-4 bg-gray-400 rounded" />
                <span className="text-xs text-gray-400">List</span>
              </div>
            </div>
          )}

          {/* Export Buttons Skeleton */}
          <div className="h-8 w-16 bg-gray-200 rounded animate-pulse opacity-50" />
          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse opacity-50" />
        </div>
      </div>

      {/* Table Content Skeleton */}
      <div className="overflow-hidden">
        <table className="w-full">
          {/* Table Header Skeleton */}
          <thead className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <tr>
              {skeletonColumns.map((_, index) => (
                <th
                  key={index}
                  className={`py-[14px] pl-3 text-left ${index === 0 ? "rounded-tl-lg" : ""
                    } ${index === skeletonColumns.length - 1 ? "rounded-tr-lg" : ""
                    }`}>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body Skeleton */}
          <tbody>
            {skeletonRows.map((_, rowIndex) => (
              <tr
                key={rowIndex}
                className="bg-white hover:bg-gray-50 border-b border-gray-100">
                {skeletonColumns.map((_, colIndex) => (
                  <td
                    key={colIndex}
                    className={`py-3 px-3 whitespace-nowrap ${rowIndex === skeletonRows.length - 1 && colIndex === 0
                      ? "rounded-bl-lg"
                      : ""
                      } ${rowIndex === skeletonRows.length - 1 &&
                        colIndex === skeletonColumns.length - 1
                        ? "rounded-br-lg"
                        : ""
                      }`}>
                    <div
                      className={`h-4 bg-gray-200 rounded animate-pulse ${colIndex === 0
                        ? "w-32"
                        : colIndex === skeletonColumns.length - 1
                          ? "w-20"
                          : "w-24"
                        }`}
                      style={{
                        animationDelay: `${rowIndex * 0.1 + colIndex * 0.05}s`,
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Skeleton */}
      <div className="flex justify-center px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
};
